import { normalizeFr, stemFr, tokenizeWords } from './content-score/tokenize-fr';
import { FR_STOPWORDS } from './content-score/wordlists-fr';
import type { SeoBrief, SeoBriefFact } from './seo-brief.schema';

/** Hôte d'une URL, sans `www.` ; chaîne vide pour un lien relatif ou une URL invalide. */
export function factSourceHost(url: string | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

export function isInternalHost(host: string, internalHosts: readonly string[]): boolean {
  if (host.length === 0) return true;
  return internalHosts.some((h): boolean => {
    const own = h.toLowerCase().replace(/^www\./, '');
    return host === own || host.endsWith(`.${own}`);
  });
}

/**
 * Faits du brief qui viennent d'une page EXTERNE lue par le moteur. Règle d'origine des
 * chiffres (2026-09-05) : un tel fait n'est publiable qu'avec un lien vers sa source,
 * sinon un chiffre marketing d'un tiers devient une affirmation de l'auteur. Le prompt
 * rédacteur impose le lien, le juge l'autorise, la règle `EXTERNAL_SOURCE` le mesure.
 * Depuis le 2026-10-09, seule une source neutre (organisme public, aide Google, étude) peut
 * entrer dans le brief : une page concurrente n'est jamais ni citée ni liée.
 */
export function externalSourcedFacts(
  brief: Pick<SeoBrief, 'facts'>,
  internalHosts: readonly string[],
): (SeoBriefFact & { source_url: string })[] {
  return brief.facts.filter((f): f is SeoBriefFact & { source_url: string } => {
    const host = factSourceHost(f.source_url);
    return host.length > 0 && !isInternalHost(host, internalHosts);
  });
}

function subjectStems(text: string): Set<string> {
  return new Set(
    tokenizeWords(normalizeFr(text))
      .filter((w): boolean => w.length > 2 && !FR_STOPWORDS.has(w) && !/^\d+$/.test(w))
      .map(stemFr),
  );
}

export interface FactRelevanceQuery {
  /** Requête de l'article (et sa zone) : une racine commune compte triple. */
  primary: string[];
  /** Plan, expressions secondaires, questions : une racine commune compte une fois. */
  secondary?: string[];
}

/** Part du meilleur score en dessous de laquelle un fait est écarté (« coupe femme » pour « coupe homme »). */
const RELATIVE_FLOOR = 0.6;

/**
 * Racines qui ne désignent aucun sujet à elles seules (2026-10-21) : « prix » dans « prix teinture
 * des cils » ne parle pas du prix du logiciel. Un fait dont le seul point commun avec l'article est
 * l'une d'elles n'est pas pertinent ; elles ne comptent qu'une fois un vrai sujet partagé (le
 * logiciel, l'abonnement, la prestation).
 */
const GENERIC_FACT_STEMS: ReadonlySet<string> = new Set(
  ['prix', 'tarif', 'tarifs', 'cout', 'couts', 'budget', 'combien', 'cher', 'gratuit', 'mois', 'mensuel']
    .map(normalizeFr)
    .map(stemFr),
);

/**
 * Faits du profil pertinents pour un article (2026-10-08) : score de racines communes entre les
 * sujets du fait (ou sa phrase) et la requête / le plan, seuil relatif au meilleur, plafond `max`.
 * Un mot générique (prix, tarif, coût…) partagé ne suffit pas : il faut au moins une racine de sujet
 * commune, sinon les tarifs du logiciel entrent dans un article sur le prix d'une prestation.
 * Aucun fait pertinent = liste vide : le rédacteur reste qualitatif plutôt que de citer un prix
 * hors sujet.
 */
export function selectRelevantFacts<T extends { statement: string; topics?: string[] }>(
  facts: readonly T[],
  query: FactRelevanceQuery,
  max = 8,
): T[] {
  const primary = subjectStems(query.primary.join(' '));
  const secondary = subjectStems((query.secondary ?? []).join(' '));
  const scored = facts
    .map((fact, index): { fact: T; index: number; score: number } => {
      const subject = subjectStems((fact.topics && fact.topics.length > 0 ? fact.topics : [fact.statement]).join(' '));
      let score = 0;
      let anchored = false;
      for (const stem of subject) {
        const weight = primary.has(stem) ? 3 : secondary.has(stem) ? 1 : 0;
        if (weight === 0) continue;
        score += weight;
        if (!GENERIC_FACT_STEMS.has(stem)) anchored = true;
      }
      return { fact, index, score: anchored ? score : 0 };
    })
    .filter((s): boolean => s.score > 0);
  if (scored.length === 0) return [];
  const floor = Math.ceil(Math.max(...scored.map((s): number => s.score)) * RELATIVE_FLOOR);
  return scored
    .filter((s): boolean => s.score >= floor)
    .sort((a, b): number => b.score - a.score || a.index - b.index)
    .slice(0, max)
    .map((s): T => s.fact);
}
